import { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Card, Typography, Box, Stack, useTheme } from '@mui/material';
import { Scrollbar } from '../../../components';
import { EmptyContent } from '../../../components/empty-content';
// store
import { useSelector } from '../../../redux/store';
import { ChatPhotoThumb, ChatPhotoLightbox } from './ChatTranscriptPhotos';
import { collectPhotos } from './chatPhotos';
// style
import { cardStyle, conversationStartStackStyle, conversationEndStackStyle, choiceItemStyle } from '../../../styles/AutoMessageAccordianStyle';

// -------------------------------------------------------------------------------------------

const ChatbotConversationsMessages = forwardRef(({ leadDetail }) => {
    const theme = useTheme();
    const { chatbotConversation } = useSelector((state) => state.lead);
    const { practiceId: storePracticeId } = useSelector((state) => state.practiceTreatmentList);
    const practiceId = storePracticeId || leadDetail?.practice?.id || leadDetail?.practice;
    const leadId = leadDetail?.id;
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const transcript = chatbotConversation?.results[0]?.chatbot_conversations[0]?.transcript;
    let temp = null;
    try { temp = transcript && JSON.parse(transcript); } catch { /* invalid transcript JSON */ }

    const photos = collectPhotos(temp);

    return (
        <Card sx={{ ...cardStyle, boxShadow: 'none' }}>
            <Scrollbar sx={{ position: 'relative', maxHeight: '600px' }}>
                <Box>
                    {!temp &&
                        <EmptyContent
                            title="No conversation found"
                            imgUrl="/assets/icons/empty/ic_email_selected.svg"
                            sx={{
                                borderRadius: 1.5,
                                bgcolor: 'background.default',
                            }}
                        />
                    }
                    {Array.isArray(temp) && temp.map((message, index) => (
                        <Box key={index} sx={{ marginBottom: 4 }}>
                            {message.sender === 'assistant' ? (
                                <Stack alignItems='flex-start'>
                                    <Stack sx={conversationStartStackStyle(theme)}>
                                        {message.message.type === 'text' &&
                                            <Typography sx={{ fontSize: '14px', 'div': { p: '0 !important' }, 'iframe': { position: 'relative !important', minHeight: '270px' } }} dangerouslySetInnerHTML={{ __html: message.message.content }} />
                                        }
                                        {message.message.type === 'single-choice' &&
                                            <Box sx={{ width: '295px' }}>
                                                <Typography sx={{ fontSize: '14px' }}>{message.message.content.text}</Typography>
                                                <Box sx={{ m: '16px 0 0' }}>
                                                    {message?.message?.content?.choices?.map((choice) => (
                                                        <Box key={choice?.title} sx={choiceItemStyle(theme)}>
                                                            {choice?.value}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        }
                                    </Stack>
                                </Stack>
                            ) : (
                                <Stack alignItems='flex-end'>
                                    <Stack sx={conversationEndStackStyle(theme)}>
                                        {message.message.type === 'text' &&
                                            <Typography sx={{ fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: message.message.content }} />
                                        }
                                        {message.message.type === 'selectedOption' &&
                                            <Typography sx={{ fontSize: '14px' }}>{message.message.content.value}</Typography>
                                        }
                                        {message.message.type === 'input' &&
                                            <Typography sx={{ fontSize: '14px' }}>{message.message.content}</Typography>
                                        }
                                        {message.message.type === 'photo' &&
                                            <ChatPhotoThumb
                                                practiceId={practiceId}
                                                leadId={leadId}
                                                photo={{ photoId: message.message.content?.photo_id, angle: message.message.content?.angle }}
                                                onOpen={() => {
                                                    const at = photos.findIndex((p) => p.photoId === message.message.content?.photo_id);
                                                    if (at >= 0) setLightboxIndex(at);
                                                }}
                                            />
                                        }
                                    </Stack>
                                </Stack>
                            )}
                        </Box>
                    ))}
                </Box>
            </Scrollbar>
            <ChatPhotoLightbox
                practiceId={practiceId}
                leadId={leadId}
                photos={photos}
                index={lightboxIndex}
                onIndexChange={setLightboxIndex}
                onClose={() => setLightboxIndex(null)}
            />
        </Card>
    );
});

ChatbotConversationsMessages.propTypes = {
    leadDetail: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), practice: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]) }),
};
ChatbotConversationsMessages.defaultProps = { leadDetail: undefined };

export default ChatbotConversationsMessages;
